// === 상수 ===
export const STAGE = {
	INVALID: 0,
	DEEP: 1,
	LIGHT: 2,
	REM: 3,
	AWAKE_IN_BED: 4,
	AWAKE_OTHER: 5,
	ERROR: 9
};

export const STAGE_LABELS = {
	[STAGE.INVALID]: '신호없음',
	[STAGE.DEEP]: 'Deep',
	[STAGE.LIGHT]: 'Light',
	[STAGE.REM]: 'REM',
	[STAGE.AWAKE_IN_BED]: 'Awake',
	[STAGE.AWAKE_OTHER]: 'Awake',
	[STAGE.ERROR]: '신호없음',
};

export const STAGE_COLORS = {
	[STAGE.INVALID]: '#94a3b8',
	[STAGE.DEEP]: '#6366f1',
	[STAGE.LIGHT]: '#818cf8',
	[STAGE.REM]: '#f472b6',
	[STAGE.AWAKE_IN_BED]: '#fbbf24',
	[STAGE.AWAKE_OTHER]: '#fbbf24',
	[STAGE.ERROR]: '#94a3b8',
};

// Samsung Health stage 문자열 → 숫자 매핑
export const SAMSUNG_STAGE_MAP = {
	'DEEP': STAGE.DEEP,
	'LIGHT': STAGE.LIGHT,
	'REM': STAGE.REM,
	'AWAKE': STAGE.AWAKE_IN_BED,
};

export const POSITION = {
	NOSIGNAL: 0,
	RIGHT: 1,
	SUPINE: 2,
	LEFT: 3,
	ERROR: 9,
};

export const POSITION_LABELS = {
	[POSITION.NOSIGNAL]: 'No Signal',
	[POSITION.RIGHT]: 'Right',
	[POSITION.SUPINE]: 'Supine',
	[POSITION.LEFT]: 'Left',
};

// Y축 값 매핑 (차트 표시용, 낮은 값 = 깊은 수면)
export const STAGE_Y_VALUE = {
	[STAGE.INVALID]: 5,
	[STAGE.DEEP]: 1,
	[STAGE.LIGHT]: 2,
	[STAGE.REM]: 3,
	[STAGE.AWAKE_IN_BED]: 4,
	[STAGE.AWAKE_OTHER]: 4,
	[STAGE.ERROR]: 5,
};

// === 파싱 함수 ===

/**
 * raw_sleep_stage 파싱
 * @param {string} rawStr - "44431221133344..."
 * @param {Date|number} startTime - 수면 시작 시각
 * @returns {Array<{time: number, stage: number, label: string, yValue: number}>}
 */
export function parseSleepStage(rawStr, startTime) {
	if (!rawStr) return [];
	const startMs = startTime instanceof Date ? startTime.getTime() : startTime;
	return Array.from(rawStr).map((ch, i) => {
		const stage = parseInt(ch);
		return {
			time: startMs + i * 60000,
			stage,
			label: STAGE_LABELS[stage] || 'Unknown',
			yValue: STAGE_Y_VALUE[stage] || 4,
		};
	});
}

/**
 * Samsung Health stages를 동일 형식으로 변환
 * @param {Array} stages - [{startTime, endTime, stage}]
 * @returns {Array<{time: number, stage: number, label: string, yValue: number}>}
 */
export function parseSamsungStages(stages) {
	if (!stages || stages.length === 0) return [];
	const result = [];
	for (const s of stages) {
		const stageNum = SAMSUNG_STAGE_MAP[s.stage] || STAGE.AWAKE_IN_BED;
		// 30초 단위로 보간
		for (let t = s.startTime; t < s.endTime; t += 30000) {
			result.push({
				time: t,
				stage: stageNum,
				label: STAGE_LABELS[stageNum],
				yValue: STAGE_Y_VALUE[stageNum] || 4,
			});
		}
	}
	return result.sort((a, b) => a.time - b.time);
}

/**
 * raw_heart_rate Hex 파싱
 * @param {string} hexStr - "3A4050..."
 * @param {Date|number} startTime
 * @returns {Array<{time: number, min: number, avg: number, max: number}>}
 */
export function parseHeartRate(hexStr, startTime) {
	if (!hexStr) return [];
	const startMs = startTime instanceof Date ? startTime.getTime() : startTime;
	const points = [];
	for (let i = 0; i < hexStr.length; i += 6) {
		if (i + 5 >= hexStr.length) break;
		const minVal = parseInt(hexStr.substr(i, 2), 16);
		const avgVal = parseInt(hexStr.substr(i + 2, 2), 16);
		const maxVal = parseInt(hexStr.substr(i + 4, 2), 16);
		points.push({
			time: startMs + (i / 6) * 30000,
			min: Math.round(40 + (minVal * 160 / 255)),
			avg: Math.round(40 + (avgVal * 160 / 255)),
			max: Math.round(40 + (maxVal * 160 / 255)),
		});
	}
	return points;
}

/**
 * raw_sdnn Hex 파싱
 * @param {string} hexStr
 * @param {Date|number} startTime
 * @returns {Array<{time: number, sdnn: number}>}
 */
export function parseSDNN(hexStr, startTime) {
	if (!hexStr) return [];
	const startMs = startTime instanceof Date ? startTime.getTime() : startTime;
	const points = [];
	for (let i = 0; i < hexStr.length; i += 2) {
		const val = parseInt(hexStr.substr(i, 2), 16);
		points.push({
			time: startMs + (i / 2) * 30000,
			sdnn: val,
		});
	}
	return points;
}

/**
 * raw_sleep_position 파싱
 * @param {string} rawStr
 * @param {Date|number} startTime
 * @returns {Array<{time: number, position: number}>}
 */
export function parsePosition(rawStr, startTime) {
	if (!rawStr) return [];
	const startMs = startTime instanceof Date ? startTime.getTime() : startTime;
	return Array.from(rawStr).map((ch, i) => ({
		time: startMs + i * 30000,
		position: parseInt(ch),
	}));
}

/**
 * DB에 저장된 시간 문자열을 타임존 변환 없이 로컬 시간으로 파싱
 * (time_start, time_end 등은 이미 로컬 시간대가 반영된 값이므로 UTC 변환 방지)
 * @param {string} dateStr - "2024-03-15T23:30:00" 또는 "2024-03-15T23:30:00Z" 형식
 * @returns {number} Unix timestamp (ms)
 */
export function parseLocalTimeMs(dateStr) {
	if (!dateStr) return 0;
	const s = String(dateStr);
	const m = s.match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):?(\d{2})?/);
	if (!m) return new Date(s).getTime();
	// new Date(year, monthIndex, day, h, m, s)는 항상 로컬 시간으로 생성
	return new Date(
		parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]),
		parseInt(m[4]), parseInt(m[5]), parseInt(m[6] || '0')
	).getTime();
}

/**
 * 초 → 시:분:초 포맷
 */
export function formatSeconds(seconds) {
	if (!seconds && seconds !== 0) return '-';
	const h = Math.floor(seconds / 3600);
	const m = Math.floor((seconds % 3600) / 60);
	const s = Math.floor(seconds % 60);
	if (h > 0) return `${h}h ${m}m`;
	if (m > 0) return `${m}m ${s}s`;
	return `${s}s`;
}

/**
 * Unix ms → 시:분 포맷
 */
export function formatTime(ms) {
	if (!ms) return '-';
	const d = new Date(ms);
	return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * 수면 단계 데이터에서 색상 세그먼트 배열 생성 (배경 영역용)
 */
export function getStageSegments(stageData) {
	if (!stageData || stageData.length === 0) return [];
	const segments = [];
	let currentSegment = null;

	for (const point of stageData) {
		if (!currentSegment || currentSegment.stage !== point.stage) {
			if (currentSegment) {
				currentSegment.endTime = point.time;
				segments.push(currentSegment);
			}
			currentSegment = {
				stage: point.stage,
				startTime: point.time,
				endTime: point.time + 30000,
				color: STAGE_COLORS[point.stage] || '#cccccc',
				label: point.label,
			};
		} else {
			currentSegment.endTime = point.time + 30000;
		}
	}

	if (currentSegment) {
		segments.push(currentSegment);
	}

	return segments;
}
